/**
 * SubmittedPhotos.tsx — the patient's weight photos, read-only, for the deal
 * drawer's Questionnaire tab.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY IT EXISTS (2026-08-29, CTO on stage: "they can't see the photos").
 * Until now the ONLY place any staff member could see an uploaded weight photo
 * was the clinician Review tab's gallery, inside `WeightPhotoSection`. An admin
 * looking at Overview or Questionnaire — the two tabs that are actually theirs —
 * saw no photo at all, only the "Photo chased N times" counter, which reports
 * on chasing rather than on what arrived. The read endpoint was never the
 * problem: `GET /v1/clinic/assessments/:id/photos` has always been
 * `@Roles('CLINICIAN','ADMIN')`. The surface was missing, not the permission.
 *
 * WHY NOT REUSE `WeightPhotoSection`. That component is the clinician's
 * ATTESTATION surface: it takes the review, the order case and the photo-review
 * status, it owns the approve gate, and it stamps "photos reviewed" on the
 * order. None of that belongs on a tab whose whole job is showing what the
 * patient sent. Reusing it would put an attestation control in front of an
 * admin, which is precisely the clinical-safety boundary this fix must respect:
 * an admin may READ everything the patient submitted and DECIDE nothing.
 *
 * ⚠️ THE FULL-SIZE PHOTO OPENS IN `PhotoLightbox`, NOT A NEW TAB. This file
 * used to justify a `<a href={signedUrl} target="_blank">` as avoiding a second
 * modal over the drawer. That reasoning was wrong twice over: `DialogShell`
 * already owns role/aria-modal/Escape/focus-return (the clinician gallery had
 * been stacking it over this same drawer for days), and the `href` put a signed
 * read url — a bearer capability to Article 9 health imagery, needing no
 * session — into the address bar, browser history and the copy-link menu. See
 * PhotoLightbox for the whole argument.
 *
 * THE URLS EXPIRE, and that is the one piece of behaviour worth copying from
 * the clinician gallery. A signed read url is short-lived, so a drawer left
 * open long enough will hit dead links. Each tile handles its own `onError` by
 * swapping to a plain "preview expired" note, and one control re-mints the
 * whole set. Never a broken-image icon — that reads as "this patient's photo is
 * corrupt" rather than "this link timed out".
 *
 * ABSENT IS NOT EMPTY. A failed read says so and offers the retry; it never
 * renders the "no photo yet" line, because an outage is not evidence that the
 * patient uploaded nothing — and a staff member who acts on that difference
 * (chasing someone who already sent their photo) is exactly the annoyance this
 * tab is meant to remove.
 */
import { useCallback, useEffect, useState } from 'react';
import { getAssessmentPhotos } from '../api/clinic';
import type { AssessmentPhotoEntry } from '../api/types';
import { Help } from '../ui/kit';
import { PhotoLightbox, photoAlt } from '../photos/PhotoLightbox';

type State =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; photos: AssessmentPhotoEntry[] };

export function SubmittedPhotos({ assessmentId }: { assessmentId: string | null }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  // Tiles whose <img> fired onError — an expired url keeps its place in the
  // grid as a note, so the count a staff member reads stays honest.
  const [dead, setDead] = useState<number[]>([]);
  // Which photo the viewer is showing, or null when it is closed.
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(() => {
    if (!assessmentId) return;
    setDead([]);
    // Close the viewer before the set it indexes into is replaced — a stale
    // index would otherwise point at a different patient's photo after a
    // reload, or past the end of a shorter set.
    setOpen(null);
    setState({ status: 'loading' });
    getAssessmentPhotos(assessmentId)
      .then(({ photos }) => setState({ status: 'ready', photos: photos ?? [] }))
      .catch(() => setState({ status: 'error' }));
  }, [assessmentId]);

  useEffect(() => {
    load();
  }, [load]);

  // No assessment means there is nothing a photo could hang off — render
  // nothing rather than an empty section that implies one is missing.
  if (!assessmentId) return null;

  return (
    <section style={{ display: 'grid', gap: '10px' }} aria-label="Weight photos the patient submitted">
      <h3 style={{ margin: 0, fontSize: '13.5px', fontWeight: 700, color: 'var(--gps-text)' }}>
        Weight photos
      </h3>

      {state.status === 'loading' ? <Help>Loading the photos…</Help> : null}

      {state.status === 'error' ? (
        <Help>
          We could not load the photos just now — this does not mean the patient has not sent any.{' '}
          <button type="button" onClick={load} style={linkButton}>
            Try again
          </button>
        </Help>
      ) : null}

      {state.status === 'ready' && state.photos.length === 0 ? (
        <Help>
          No photo uploaded yet. The patient can add one from their account, and the Overview tab
          shows how many times they have been chased for it.
        </Help>
      ) : null}

      {state.status === 'ready' && state.photos.length > 0 ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '10px',
            }}
          >
            {state.photos.map((photo, index) =>
              photo.photoUrl === null || dead.includes(index) ? (
                <div key={index} style={expiredTile}>
                  Preview expired
                </div>
              ) : (
                // A BUTTON, never an anchor: the signed url stays a JS value
                // and an `img src`, so it never reaches the address bar, the
                // history, or the copy-link menu (see PhotoLightbox).
                <button
                  key={index}
                  type="button"
                  onClick={() => setOpen(index)}
                  aria-label={`Open ${photoAlt(index, state.photos.length)}`}
                  style={{
                    display: 'block',
                    padding: 0,
                    borderRadius: 'var(--gps-r-md)',
                    overflow: 'hidden',
                    border: '1px solid var(--gps-border)',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    src={photo.photoUrl}
                    alt={photoAlt(index, state.photos.length)}
                    onError={() => setDead((d) => (d.includes(index) ? d : [...d, index]))}
                    style={{ display: 'block', width: '100%', height: '140px', objectFit: 'cover' }}
                  />
                </button>
              ),
            )}
          </div>
          <Help style={{ margin: 0 }}>
            Read-only. Confirming a photo against the patient&rsquo;s record is a clinician action and
            happens on the Review tab.{' '}
            <button type="button" onClick={load} style={linkButton}>
              Reload photos
            </button>
          </Help>

          {/* The viewer. Indices are into the SAME array the grid renders, so
              Previous/Next walk the photos in the order shown. An expired url
              closes it and marks that tile — the note and the Reload control
              then sit together in the grid. */}
          {open !== null ? (
            <PhotoLightbox
              /* url + WHO, one object per photo — the viewer renders the role
                 beside the image, so the two must not be able to drift apart
                 (see PhotoLightboxItem). */
              photos={state.photos.map((p, i) => ({
                url: dead.includes(i) ? null : p.photoUrl,
                addedByRole: p.addedByRole,
              }))}
              index={open}
              onIndex={setOpen}
              onClose={() => setOpen(null)}
              onExpired={(i) => setDead((d) => (d.includes(i) ? d : [...d, i]))}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

const linkButton: React.CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'var(--gps-link)',
  textDecoration: 'underline',
  cursor: 'pointer',
};

const expiredTile: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '140px',
  borderRadius: 'var(--gps-r-md)',
  border: '1px dashed var(--gps-border)',
  background: 'var(--gps-surface-2)',
  color: 'var(--gps-muted)',
  fontSize: '13px',
  textAlign: 'center',
  padding: '8px',
};
